import React from "react";
import _ from "lodash";

import { Badge, Box, Button, Mark, Menu, MenuItem, Slider, TextField, Typography } from "@material-ui/core";
import FilterListIcon from "@material-ui/icons/FilterList";
import { withStyles } from "@material-ui/core/styles";

import Spacer from "components/Spacer";

import { OperatorStatus } from "modules/customer/tools/product/operator";
import {
    selectIsOperatorsFilterModified,
    selectOperatorStatuses,
    selectOperatorsHeadroom,
    selectOperatorsOptimisedSales,
    selectOperatorsTotalEstimatedSales,
    selectOperatorsClientSourcedSales,
    selectOperatorsFilter,
    setOperatorsFilter,
    clearOperatorsFilter,
    OperatorsFilter
} from "modules/customer/tools/product/partnerFilters/partnerFiltersSlice";
import { selectUserInfo } from "modules/auth/authSlice";
import { useAppDispatch, useAppSelector } from "store";
import numberFormatter from "utils/numberFormatter";

const StyledMenu = withStyles(theme => ({
    paper: {
        paddingLeft: theme.spacing(1.5),
        paddingRight: theme.spacing(1.5)
    },
    list: {
        width: theme.spacing(38)
    }
}))(Menu);

const StyledTextField = withStyles(theme => ({
    root: {
        "& .MuiOutlinedInput-root": {
            // @ts-ignore
            backgroundColor: theme.palette.quaternary.main
        }
    }
}))(TextField);

const Filter: React.FC = () => {
    const dispatch = useAppDispatch();
    const userInfo = useAppSelector(selectUserInfo);
    const statuses = useAppSelector(selectOperatorStatuses);
    const headroom = useAppSelector(selectOperatorsHeadroom);
    const optimisedSales = useAppSelector(selectOperatorsOptimisedSales);
    const totalEstimatedSales = useAppSelector(selectOperatorsTotalEstimatedSales);
    const clientSourcedSales = useAppSelector(selectOperatorsClientSourcedSales);
    const operatorsFilter = useAppSelector(selectOperatorsFilter);
    const isOperatorsFilterModified = useAppSelector(selectIsOperatorsFilterModified);
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);
    const [/*currentOperatorsFilter*/, setCurrentOperatorsFilter] = React.useState<OperatorsFilter>(operatorsFilter);

    // Headroom slider values
    const headroomPercentileThresholds = headroom.percentileThresholds;
    const headroomSliderMinValue = headroomPercentileThresholds.indexOf(Math.max(headroom.minPercentileThreshold, operatorsFilter.headroom[0]));
    const headroomSliderMaxValue = headroomPercentileThresholds.indexOf(Math.min(headroom.maxPercentileThreshold, operatorsFilter.headroom[1]));
    const headroomSliderMarks = React.useMemo(() => {
        const marks: Mark[] = [];

        for (let i = 0; i < headroomPercentileThresholds.length; i++) {
            const value = i;
            const label = numberFormatter.toGBP(headroomPercentileThresholds[i], 0);
            marks.push({ value, label });
        }
        return marks;
    }, [headroomPercentileThresholds]);

    // Optimised sales slider values
    const optimisedSalesPercentileThresholds = optimisedSales.percentileThresholds;
    const optimisedSalesSliderMinValue = optimisedSalesPercentileThresholds.indexOf(Math.max(optimisedSales.minPercentileThreshold, operatorsFilter.optimisedSales[0]));
    const optimisedSalesSliderMaxValue = optimisedSalesPercentileThresholds.indexOf(Math.min(optimisedSales.maxPercentileThreshold, operatorsFilter.optimisedSales[1]));
    const optimisedSalesSliderMarks = React.useMemo(() => {
        const marks: Mark[] = [];

        for (let i = 0; i < optimisedSalesPercentileThresholds.length; i++) {
            const value = i;
            const label = numberFormatter.toGBP(optimisedSalesPercentileThresholds[i], 0);
            marks.push({ value, label });
        }
        return marks;
    }, [optimisedSalesPercentileThresholds]);

    // Total estimated sales slider values
    const totalEstimatedSalesPercentileThresholds = totalEstimatedSales.percentileThresholds;
    const totalEstimatedSalesSliderMinValue = totalEstimatedSalesPercentileThresholds.indexOf(Math.max(totalEstimatedSales.minPercentileThreshold, operatorsFilter.totalEstimatedSales[0]));
    const totalEstimatedSalesSliderMaxValue = totalEstimatedSalesPercentileThresholds.indexOf(Math.min(totalEstimatedSales.maxPercentileThreshold, operatorsFilter.totalEstimatedSales[1]));
    const totalEstimatedSalesSliderMarks = React.useMemo(() => {
        const marks: Mark[] = [];

        for (let i = 0; i < totalEstimatedSalesPercentileThresholds.length; i++) {
            const value = i;
            const label = numberFormatter.toGBP(totalEstimatedSalesPercentileThresholds[i], 0);
            marks.push({ value, label });
        }
        return marks;
    }, [totalEstimatedSalesPercentileThresholds]);

    // Client sourced sales slider values
    const clientSourcedSalesPercentileThresholds = clientSourcedSales.percentileThresholds;
    const clientSourcedSalesSliderMinValue = clientSourcedSalesPercentileThresholds.indexOf(Math.max(clientSourcedSales.minPercentileThreshold, operatorsFilter.clientSourcedSales[0]));
    const clientSourcedSalesSliderMaxValue = clientSourcedSalesPercentileThresholds.indexOf(Math.min(clientSourcedSales.maxPercentileThreshold, operatorsFilter.clientSourcedSales[1]));
    const clientSourcedSalesSliderMarks = React.useMemo(() => {
        const marks: Mark[] = [];

        for (let i = 0; i < clientSourcedSalesPercentileThresholds.length; i++) {
            const value = i;
            const label = numberFormatter.toGBP(clientSourcedSalesPercentileThresholds[i], 0);
            marks.push({ value, label });
        }
        return marks;
    }, [clientSourcedSalesPercentileThresholds]);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleResetAllClick = () => {
        dispatch(clearOperatorsFilter());
    };

    const setOperatorsFilterDelayed = React.useMemo(() => {
        return _.debounce((operatorsFilter: OperatorsFilter) => dispatch(setOperatorsFilter(operatorsFilter)), 400);
    }, [dispatch]);

    const handleOperatorStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const status = event.target.value as OperatorStatus;
        const newOperatorsFilter = { ...operatorsFilter, status };
        setCurrentOperatorsFilter(newOperatorsFilter);
        setOperatorsFilterDelayed(newOperatorsFilter);
    };

    const handleHeadroomChange = (event: React.ChangeEvent<{}>, value: any) => {
        const headroom = [headroomPercentileThresholds[value[0]], headroomPercentileThresholds[value[1]]];
        const newOperatorsFilter = { ...operatorsFilter, headroom };
        setCurrentOperatorsFilter(newOperatorsFilter);
        setOperatorsFilterDelayed(newOperatorsFilter);
    };

    const handleOptimisedSalesChange = (event: React.ChangeEvent<{}>, value: any) => {
        const optimisedSales = [optimisedSalesPercentileThresholds[value[0]], optimisedSalesPercentileThresholds[value[1]]];
        const newOperatorsFilter = { ...operatorsFilter, optimisedSales };
        setCurrentOperatorsFilter(newOperatorsFilter);
        setOperatorsFilterDelayed(newOperatorsFilter);
    };

    const handleTotalEstimatedSalesChange = (event: React.ChangeEvent<{}>, value: any) => {
        const totalEstimatedSales = [totalEstimatedSalesPercentileThresholds[value[0]], totalEstimatedSalesPercentileThresholds[value[1]]];
        const newOperatorsFilter = { ...operatorsFilter, totalEstimatedSales };
        setCurrentOperatorsFilter(newOperatorsFilter);
        setOperatorsFilterDelayed(newOperatorsFilter);
    };

    const handleClientSourcedSalesChange = (event: React.ChangeEvent<{}>, value: any) => {
        const clientSourcedSales = [clientSourcedSalesPercentileThresholds[value[0]], clientSourcedSalesPercentileThresholds[value[1]]];
        const newOperatorsFilter = { ...operatorsFilter, clientSourcedSales };
        setCurrentOperatorsFilter(newOperatorsFilter);
        setOperatorsFilterDelayed(newOperatorsFilter);
    };

    React.useEffect(() => {
        setCurrentOperatorsFilter(operatorsFilter);
    }, [operatorsFilter]);

    return (
        <>
            <Badge color="primary" variant="dot" overlap="rectangular" invisible={!isOperatorsFilterModified}>
                <Button
                    variant="text"
                    size="small"
                    color="default"
                    disableElevation
                    startIcon={<FilterListIcon />}
                    onClick={handleClick}
                    data-cy="btn-filter"
                >
                    Filter
                </Button>
            </Badge>
            <StyledMenu
                open={open}
                onClose={handleClose}
                keepMounted
                anchorEl={anchorEl}
                getContentAnchorEl={null}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right"
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right"
                }}
            >
                <Box paddingBottom={2} paddingRight={2} paddingLeft={2}>
                    <Box display="flex">
                        <Spacer />
                        <Button
                            variant="text"
                            size="small"
                            color="default"
                            disableElevation
                            onClick={handleResetAllClick}
                        >
                            Reset all
                        </Button>
                    </Box>
                    <Box padding={1}>
                        <Typography variant="body1" component="div" gutterBottom>
                            Operator status
                        </Typography>
                        <StyledTextField
                            variant="outlined"
                            size="small"
                            select
                            value={operatorsFilter.status}
                            onChange={handleOperatorStatusChange}
                            SelectProps={{
                                displayEmpty: true,
                                MenuProps: {
                                    getContentAnchorEl: null,
                                    anchorOrigin: {
                                        vertical: "bottom",
                                        horizontal: "left"
                                    },
                                    transformOrigin: {
                                        vertical: "top",
                                        horizontal: "left"
                                    }
                                }
                            }}
                            fullWidth
                        >
                            <MenuItem value="">
                                Select operator status
                            </MenuItem>
                            {statuses.map((status, index) =>
                                <MenuItem key={index} value={status}>
                                    {status}
                                </MenuItem>
                            )}
                        </StyledTextField>
                    </Box>
                    <Box paddingTop={2}>
                        <Typography variant="body1" component="div">
                            Headroom
                        </Typography>
                        <Slider
                            value={[headroomSliderMinValue, headroomSliderMaxValue]}
                            onChange={handleHeadroomChange}
                            step={null}
                            min={0}
                            max={5}
                            marks={headroomSliderMarks}
                        />
                    </Box>
                    <Box paddingTop={2}>
                        <Typography variant="body1" component="div">
                            Optimised sales
                        </Typography>
                        <Slider
                            value={[optimisedSalesSliderMinValue, optimisedSalesSliderMaxValue]}
                            onChange={handleOptimisedSalesChange}
                            step={null}
                            min={0}
                            max={5}
                            marks={optimisedSalesSliderMarks}
                        />
                    </Box>
                    <Box paddingTop={2}>
                        <Typography variant="body1" component="div">
                            Estimated sales
                        </Typography>
                        <Slider
                            value={[totalEstimatedSalesSliderMinValue, totalEstimatedSalesSliderMaxValue]}
                            onChange={handleTotalEstimatedSalesChange}
                            step={null}
                            min={0}
                            max={5}
                            marks={totalEstimatedSalesSliderMarks}
                        />
                    </Box>
                    <Box paddingTop={2}>
                        <Typography variant="body1" component="div">
                            {userInfo.companyDisplayName} sourced sales
                        </Typography>
                        <Slider
                            value={[clientSourcedSalesSliderMinValue, clientSourcedSalesSliderMaxValue]}
                            onChange={handleClientSourcedSalesChange}
                            step={null}
                            min={0}
                            max={5}
                            marks={clientSourcedSalesSliderMarks}
                        />
                    </Box>
                </Box>
            </StyledMenu>
        </>
    );
};

export default Filter;
